import React, { useEffect, useRef, useState } from "react";
import styles from './TimePicker.module.scss';
import PropTypes from "prop-types";
import { ReactComponent as ChevronUp } from './ChevronUp.svg';
import useClickOutside from "../../hooks/useClickOutside";
import Icon from "../icon/Icon";

const TimePicker = ({
  label,
  value,
  onChange,
  error,
  disabled = false
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [hour, setHour] = useState(null);
  const [minute, setMinute] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [hours, minutes] = value.split(":");
      setHour(parseInt(hours));
      setMinute(parseInt(minutes));
      setInputValue(value); // Initialize inputValue with the formatted time
    } else {
      setHour(null);
      setMinute(null);
      setInputValue('');
    }
  }, [value]);

  useClickOutside(containerRef, () => {
    setShowPicker((prevValue) => {
      if(prevValue === true) {
        onChange(hour !== null && minute !== null ?
          `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}` :
          null
        )
      }
      return false;
    });
  });

  // Handles user input and auto-formats the time
  const handleTimeInputChange = (e) => {
    let newValue = e.target.value;

    // Allow only digits and one colon
    if (!/^\d{0,2}(:?\d{0,2})?$/.test(newValue)) return;

    // Automatically format as HH:MM
    if (newValue.length === 2 && newValue.indexOf(':') === -1) {
      newValue += ':';
    }

    // Ensure hour is valid (0-23) and minute is valid (0-59)
    const [newHour, newMinute] = newValue.split(":");

    if (newHour && parseInt(newHour) > 23) {
      setInputValue(`23:${newMinute || '00'}`);
      return;
    }

    if (newMinute && parseInt(newMinute) > 59) {
      setInputValue(`${newHour || '00'}:59`);
      return;
    }
    
    setHour(newHour ?? null);
    setMinute(newMinute ?? null);

    setInputValue(newValue);
  };

  const handleTimeInputBlur = () => {

    // If it's a valid time, update the hour and minute
    if (/^\d{2}:\d{2}$/.test(inputValue)) {
      const [hourPart, minutePart] = inputValue.split(":");
      setHour(parseInt(hourPart));
      setMinute(parseInt(minutePart));
      if(value !== inputValue) {
        onChange(inputValue);
      }
    } else {
      onChange(null);
    }
  };

  // Increment hour, with wrapping at 23
  const incrementHour = () => {
    if (hour === null) {
      setHour(0);
    }
    if (minute === null) {
      setMinute(0);
    }
    setHour((prevHour) => (prevHour + 1) % 24);
  };

  // Decrement hour, with wrapping at 0
  const decrementHour = () => {
    if (hour === null) {
      setHour(0);
    }
    if (minute === null) {
      setMinute(0);
    }
    setHour((prevHour) => (prevHour - 1 + 24) % 24);
  };

  // Increment minute, with wrapping at 59
  const incrementMinute = () => {
    if (hour === null) {
      setHour(0);
    }
    if (minute === null) {
      setMinute(0);
    }
    setMinute((prevMinute) => (prevMinute + 1) % 60);
  };

  // Decrement minute, with wrapping at 0
  const decrementMinute = () => {
    if (hour === null) {
      setHour(0);
    }
    if (minute === null) {
      setMinute(0);
    }
    setMinute((prevMinute) => (prevMinute - 1 + 60) % 60);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      {label && (
        <label className={styles.label}>{label}</label>
      )}
      <div
        className={`
          ${styles.input} 
          ${error ? styles.errorInput : ''}
          ${disabled ? styles.disabled : ''}
        `}
        onClick={() => {
          if (!disabled) {
            setShowPicker(!showPicker);
          }
        }}
      >
        <Icon name="clock" />
        <input
          type="text"
          value={inputValue}
          onChange={handleTimeInputChange}
          onBlur={handleTimeInputBlur}
          disabled={disabled}
          placeholder="HH:MM"
          className={styles.inputField}
        />
      </div>
      {error && <span className={styles.error}>{error}</span>}
      {showPicker && (
        <div className={styles.timePicker}>
          <div className={styles.pickerItem}>
            <span onClick={incrementHour} className={styles.upArrow}>
              <ChevronUp />
            </span>
            <span>{hour === null ? '--' : hour.toString().padStart(2, '0')}</span>
            <span onClick={decrementHour} className={styles.downArrow}>
              <ChevronUp />
            </span>
          </div>

          <div>:</div>

          <div className={styles.pickerItem}>
            <span onClick={incrementMinute} className={styles.upArrow}>
              <ChevronUp />
            </span>
            <span>{minute === null ? '--' : minute.toString().padStart(2, '0')}</span>
            <span onClick={decrementMinute} className={styles.downArrow}>
              <ChevronUp />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

TimePicker.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

export default TimePicker;